/**
 * AppHeader — the strip pinned to the top of the canvas column, mirroring
 * <ChatSidebarTopBar>'s height so the two bars visually align across the app.
 *
 * Per Workplace post 1566820201741944 (Fia Soh), the canvas-side top bar
 * houses the project-scoped chrome:
 *   • back-chevron → project name (inline-editable) → canvas tabs slot
 *   • right group: [History] [Rules] [Settings gear] [Publish] [Bug]
 *     + Sparks credits chip
 *
 * Add Nodes, Assets, Download and Keyboard Shortcuts are NOT here — they
 * live in <CanvasBottomPalette> on the canvas surface. Download used to sit
 * in the right group; it moved out when the palette took over that job.
 */

import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Bug, ClipboardList, History, Pencil } from 'lucide-react'
import { FeedbackModal } from './FeedbackModal'
import { listenAppEvent, type FeedbackContextNode } from '../canvas-v2/nodes/shared/useNodeActions'
import { PublishButton } from './PublishButton'
import SparksBalance from '../layout/SparksBalance'
import { VersionHistoryPanel } from '../canvas-v2/VersionHistoryPanel'
import ProjectRulesModal from '../rules/ProjectRulesModal'
import ProjectVisibilityControl from '../teams/ProjectVisibilityControl'
import { ProjectConfigGear } from '../canvas-v2/ProjectConfigGear'
import { useCanvasVersions } from '../../hooks/useCanvasVersions'
import { useProject, useUpdateProject } from '../../api/hooks'
import { trackHeaderControlClicked } from '../../analytics/chatHeaderAnalytics'
import type { HeaderControl } from '../../analytics/generated/analyticsEvents'
import { createNadDebug } from '../../utils/debugLog'
import { cn } from '../../lib/utils'
import { WORKSPACE_TOP_BAR_HEIGHT_CLASS } from './headerHeight'
import { useBooleanFlagValue } from '../../hooks/useBooleanFlagValue'
import { DirectorDeskToolbarButton } from '../../features/director-desk/ToolbarButton'
import type { AutoConfirmCostSettings } from '../chat/autoConfirmCost'
import type { CanvasPerformanceSettings } from '../canvas-v2/canvasPerformanceSettings'
import { TrialStatusChip } from '../../features/billing/TrialStatusChip'
import { useQueryClient } from '@tanstack/react-query'

const headerLog = createNadDebug('dw:ui-rebrand-dreamwrite:app-header')
// RFC-1146 — separate namespace so `./nad trace --feature title-case-top-bar`
// scopes to the label-copy contract rather than the unrelated rebrand tag.
const labelLog = createNadDebug('dw:title-case-top-bar:labels')

interface AppHeaderProps {
  projectId: string
  projectName?: string

  // Settings the ProjectConfigGear needs.
  autoConfirmCostSettings?: AutoConfirmCostSettings
  onAutoConfirmImageCostChange?: (value: boolean) => void
  onAutoConfirmVideoCostChange?: (value: boolean) => void
  taskCompletionChimeEnabled?: boolean
  onTaskCompletionChimeEnabledChange?: (value: boolean) => void
  canvasPerformanceSettings?: CanvasPerformanceSettings
  onCanvasPerformanceSettingChange?: (
    key: keyof CanvasPerformanceSettings,
    value: boolean,
  ) => void

  // Slot for canvas tabs — owned by WorkspacePageV2 so the canvas-tabs
  // state machine stays put.
  centerContent?: ReactNode

  onOpenUsageCost: () => void
}

export const AppHeader = memo(function AppHeader({
  projectId,
  projectName,
  autoConfirmCostSettings,
  onAutoConfirmImageCostChange,
  onAutoConfirmVideoCostChange,
  taskCompletionChimeEnabled,
  onTaskCompletionChimeEnabledChange,
  canvasPerformanceSettings,
  onCanvasPerformanceSettingChange,
  centerContent,
  onOpenUsageCost,
}: AppHeaderProps) {
  const { t, i18n } = useTranslation(['canvas', 'workspace'])
  const updateProject = useUpdateProject()
  const queryClient = useQueryClient()
  const directorDeskEnabled = useBooleanFlagValue('director_desk_enabled', false)

  // RFC-1577 `project_header_control_clicked`. `editable` is false on read-only
  // demo projects and omitted until the project payload has loaded.
  const { data: projectData } = useProject(projectId)
  const editable = projectData ? projectData.is_demo !== true : undefined
  const editableRef = useRef(editable)
  editableRef.current = editable
  useEffect(() => {
    if (projectData && projectData.can_view_credits !== true) {
      queryClient.removeQueries({ queryKey: ['sparks'] })
      queryClient.removeQueries({ queryKey: ['costSummary'] })
      queryClient.removeQueries({ queryKey: ['generation-trial-status'] })
    }
  }, [projectData, queryClient])
  const trackControl = useCallback(
    (control: HeaderControl) => trackHeaderControlClicked(projectId, control, editableRef.current),
    [projectId],
  )

  // RFC-1146 — emit the RESOLVED top-bar label copy once per locale so a NAD
  // trace shows the exact strings the user saw, not just that a render
  // happened. Mount/locale-keyed on purpose: this header re-renders on every
  // canvas interaction, so a per-render log would be noise. `createNadDebug`
  // only emits under VITE_NAD_DEBUG and is tree-shaken from prod builds.
  useEffect(() => {
    labelLog({
      event: 'toolbar-labels-resolved',
      locale: i18n.language,
      projectRules: t('canvasToolbox.projectRules'),
      downloadAssets: t('canvasToolbox.downloadAssets'),
      versionHistory: t('canvasToolbox.versionHistory'),
      projectSettings: t('projectConfigGear.projectSettings'),
      publishProject: t('workspace:publishButton.publishProject'),
      sendFeedback: t('workspace:chatSidebar.sendFeedback'),
      creditBalance: t('workspace:sparksBalance.creditBalance'),
      editProjectName: t('workspace:chatSidebar.editProjectName'),
    })
  }, [i18n.language, t])

  // ── Project-name inline edit ──────────────────────────────────────
  const [isEditingName, setIsEditingName] = useState(false)
  const [editingName, setEditingName] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)
  const isCancellingRef = useRef(false)

  useEffect(() => {
    if (isEditingName && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [isEditingName])

  const handleStartEditingName = useCallback(() => {
    trackControl('rename')
    setEditingName(projectName || '')
    setIsEditingName(true)
  }, [projectName, trackControl])

  const handleSaveName = useCallback(() => {
    if (isCancellingRef.current) {
      isCancellingRef.current = false
      return
    }
    const trimmed = editingName.trim()
    if (trimmed && trimmed !== projectName) {
      updateProject.mutate({ projectId, data: { name: trimmed } })
    }
    setIsEditingName(false)
  }, [editingName, projectName, projectId, updateProject])

  const handleCancelEditingName = useCallback(() => {
    isCancellingRef.current = true
    setIsEditingName(false)
    setEditingName('')
  }, [])

  const handleEditKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSaveName()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancelEditingName()
      }
    },
    [handleSaveName, handleCancelEditingName],
  )

  // ── Right-group modals ──────────────────────────────────────────────
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)
  const [isProjectRulesOpen, setIsProjectRulesOpen] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  // Nodes the report is about when opened from the canvas node right-click
  // menu; empty when opened from the header button.
  const [feedbackContextNodes, setFeedbackContextNodes] = useState<FeedbackContextNode[]>([])

  const canvasVersions = useCanvasVersions(projectId)

  const openFeedback = useCallback(() => {
    headerLog({ event: 'right-group-click', control: 'feedback' })
    trackControl('feedback')
    setFeedbackContextNodes([])
    setShowFeedback(true)
  }, [trackControl])
  useEffect(() => listenAppEvent('open-feedback', ({ nodes }) => {
    headerLog({ event: 'feedback-opened-from-context-menu', node_count: nodes.length })
    setFeedbackContextNodes(nodes)
    setShowFeedback(true)
  }), [])
  const openVersionHistory = useCallback(() => {
    headerLog({ event: 'right-group-click', control: 'version-history' })
    trackControl('version_history')
    setIsVersionHistoryOpen(true)
  }, [trackControl])
  const openProjectRules = useCallback(() => {
    headerLog({ event: 'right-group-click', control: 'project-rules' })
    trackControl('project_rules')
    setIsProjectRulesOpen(true)
  }, [trackControl])
  const openSettings = useCallback(() => trackControl('settings'), [trackControl])
  const openPublish = useCallback(() => trackControl('publish'), [trackControl])
  const openCredits = useCallback(() => {
    trackControl('credits')
    onOpenUsageCost()
  }, [onOpenUsageCost, trackControl])

  const iconButton =
    'flex h-9 w-9 items-center justify-center rounded-lg text-chrome/70 ' +
    'hover:bg-chrome/10 hover:text-chrome transition-colors cursor-pointer'

  return (
    <>
      <header
        data-testid="app-header"
        className={cn(
          'flex w-full flex-shrink-0 items-center gap-3',
          WORKSPACE_TOP_BAR_HEIGHT_CLASS,
          'border-b border-chrome/30 bg-chrome/[0.06] px-3',
        )}
      >
        {/* Center-left: project name (inline-editable) + caller-supplied
            canvas-tabs slot. Fills the middle of the bar. (Back-chevron
            lives in the sidebar header — extreme left of the app.) */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isEditingName ? (
            <input
              ref={editInputRef}
              data-testid="project-name-input"
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={handleEditKeyDown}
              className={cn(
                'text-sm text-foreground bg-surface-2 border border-border rounded px-1.5 py-0.5',
                'outline-none focus:border-chrome/50 min-w-0 max-w-[14rem] truncate',
              )}
            />
          ) : (
            <>
              <span
                className="text-sm font-medium text-chrome truncate min-w-0 max-w-[14rem]"
                data-testid="project-name-display"
              >
                {projectName || t('workspace:chatSidebar.untitled')}
              </span>
              <button
                type="button"
                data-testid="edit-project-name-button"
                onClick={handleStartEditingName}
                className="flex-shrink-0 rounded p-1 text-chrome/70 transition-colors hover:bg-chrome/10 hover:text-chrome cursor-pointer"
                title={t('workspace:chatSidebar.editProjectName')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          {/* Sits with the project name because it describes THIS project, not
              the active team. Renders nothing until the real visibility is
              known, and nothing at all with `teams_enabled` off. */}
          {projectId && <ProjectVisibilityControl projectId={projectId} />}
          {centerContent}
        </div>

        {/* Right group: History / Rules / Settings / Publish / Bug / Sparks */}
        <div
          className="flex items-center gap-1"
          data-testid="app-header-right-group"
        >
          {directorDeskEnabled && (
            // `contents` keeps the button a direct flex item; capture runs before it navigates away.
            <span className="contents" onClickCapture={() => trackControl('director_desk')}>
              <DirectorDeskToolbarButton project={projectId} className={iconButton} />
            </span>
          )}
          <button
            type="button"
            onClick={openVersionHistory}
            className={iconButton}
            title={t('canvasToolbox.versionHistory')}
            data-testid="toolbox-version-history"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={openProjectRules}
            className={iconButton}
            title={t('canvasToolbox.projectRules')}
            aria-label={t('canvasToolbox.projectRules')}
            data-testid="toolbox-project-rules"
          >
            <ClipboardList className="h-4 w-4" />
          </button>
          <ProjectConfigGear
            autoConfirmCostSettings={autoConfirmCostSettings}
            onAutoConfirmImageCostChange={onAutoConfirmImageCostChange}
            onAutoConfirmVideoCostChange={onAutoConfirmVideoCostChange}
            taskCompletionChimeEnabled={taskCompletionChimeEnabled}
            onTaskCompletionChimeEnabledChange={onTaskCompletionChimeEnabledChange}
            canvasPerformanceSettings={canvasPerformanceSettings}
            onCanvasPerformanceSettingChange={onCanvasPerformanceSettingChange}
            onOpen={openSettings}
          />
          {projectId && <PublishButton projectId={projectId} onOpen={openPublish} />}
          <button
            type="button"
            onClick={openFeedback}
            className={iconButton}
            title={t('workspace:chatSidebar.sendFeedback')}
            data-testid="feedback-button"
          >
            <Bug className="h-4 w-4" />
          </button>
          {projectData?.can_view_credits === true && (
            <>
              <TrialStatusChip projectId={projectId} />
              <SparksBalance onClick={openCredits} />
            </>
          )}
        </div>
      </header>


      <VersionHistoryPanel
        isOpen={isVersionHistoryOpen}
        onClose={() => {
          canvasVersions.closeDiff()
          setIsVersionHistoryOpen(false)
        }}
        versions={canvasVersions.versions}
        currentVersionId={canvasVersions.head}
        onCreateVersion={canvasVersions.createVersion}
        onRestoreVersion={canvasVersions.restoreVersion}
        onDeleteVersion={canvasVersions.deleteVersion}
        onDiffVersion={canvasVersions.diffVersion}
        onCloseDiff={canvasVersions.closeDiff}
        onRefresh={canvasVersions.refresh}
        diffText={canvasVersions.diffText}
        diffVersionId={canvasVersions.diffVersionId}
        isDiffLoading={canvasVersions.isDiffLoading}
        isLoading={canvasVersions.isLoading}
      />

      <ProjectRulesModal
        isOpen={isProjectRulesOpen}
        onClose={() => setIsProjectRulesOpen(false)}
      />

      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        projectId={projectId}
        contextNodes={feedbackContextNodes}
      />
    </>
  )
})
